
import { 
    db, 
    collection, 
    query, 
    orderBy, 
    getDocs,
    addDoc,
    updateDoc,
    doc,
    deleteDoc,
    serverTimestamp,
    setDoc
} from "../../firebaseClient";
import type { GovRegAgency, GovAnnouncementStatus, ComplianceIntel } from "../types/legalOps";

export interface GovRegWatchEvent {
    id: string;
    sourceName: string;
    category: string;
    title: string;
    summary: string;
    publishedAt: string;
    url: string;
    riskLevel: string;
    status: string;
}

const COLLECTION_SOURCES = "govRegSources";
const COLLECTION_ANNOUNCEMENTS = "govRegAnnouncements";

// --- System Locked Agencies (Cannot be deleted, only disabled) ---
export const LOCKED_AGENCIES: GovRegAgency[] = [
  { 
      id: "pdpc-prep", 
      name: "個人資料保護委員會籌備處", 
      shortName: "個資籌備處", 
      baseUrl: "https://www.ndc.gov.tw", 
      isActive: true, 
      enabled: true,
      note: "【系統鎖定】主管全公司個資法規及相關子法，優先追蹤。"
  },
  { id: "tfda", name: "衛生福利部食品藥物管理署", shortName: "食藥署", baseUrl: "https://www.fda.gov.tw", isActive: true, enabled: true },
  { id: "osha", name: "職業安全衛生署", shortName: "職安署", baseUrl: "https://www.osha.gov.tw", isActive: true, enabled: true },
  { id: "moenv", name: "環境部", shortName: "環境部", baseUrl: "https://www.moenv.gov.tw", isActive: true, enabled: true }
];

export async function listGovAgencies(includeInactive = true): Promise<GovRegAgency[]> {
    try {
        // 1. Fetch Custom Agencies from Firestore
        let q = query(collection(db, COLLECTION_SOURCES), orderBy("createdAt", "asc"));
        const snap = await getDocs(q);
        const customList = snap.docs.map(d => ({ id: d.id, ...d.data() } as GovRegAgency));

        // 2. Merge with Locked Agencies
        // If a locked agency exists in Firestore (e.g. user modified enabled status), use Firestore version.
        // Otherwise use default.
        const mergedList = [...customList];
        
        LOCKED_AGENCIES.forEach(locked => {
            const exists = customList.find(c => c.id === locked.id);
            if (!exists) {
                // Not in DB yet, show default
                mergedList.unshift(locked);
            }
        });
        
        // Filter
        if (!includeInactive) {
            return mergedList.filter(a => a.isActive !== false && a.enabled !== false);
        }
        
        return mergedList;
    } catch (e) {
        console.error("listGovAgencies error", e);
        return LOCKED_AGENCIES; // Fallback
    }
}

export async function createGovAgency(input: Omit<GovRegAgency, 'createdAt' | 'updatedAt'>): Promise<void> {
    // Prevent overwriting locked IDs with create
    if (LOCKED_AGENCIES.some(l => l.id === input.id)) {
        throw new Error("此 ID 為系統保留，請直接啟用該機關。");
    }

    if (input.id) {
        await setDoc(doc(db, COLLECTION_SOURCES, input.id), {
            ...input,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        });
    } else {
        await addDoc(collection(db, COLLECTION_SOURCES), {
            ...input,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        });
    }
}

export async function updateGovAgency(id: string, input: Partial<GovRegAgency>): Promise<void> {
    // For locked agencies, we basically create/update the doc in Firestore to override defaults
    // Use set with merge to handle cases where doc doesn't exist yet
    await setDoc(doc(db, COLLECTION_SOURCES, id), {
        ...input,
        updatedAt: serverTimestamp()
    }, { merge: true });
}

export async function deleteGovAgency(id: string): Promise<void> {
    // Prevent deleting locked agencies
    if (LOCKED_AGENCIES.some(l => l.id === id)) {
        throw new Error("系統預設機關無法刪除，請改用「停用」。");
    }
    await deleteDoc(doc(db, COLLECTION_SOURCES, id));
}

// --- Announcement Management (Legacy support) ---
// (Kept simplified for compatibility)
export async function listGovAnnouncementsByStatus(status: GovAnnouncementStatus | string): Promise<ComplianceIntel[]> {
    return []; 
}
export async function updateAnnouncementStatus(id: string, status: GovAnnouncementStatus): Promise<void> {}
export async function fetchGovAnnouncements(status = 'new'): Promise<GovRegWatchEvent[]> { return []; }
